import React, { useMemo } from 'react';
import { PDOChecklistItem } from './pdoCheckList';

import withPDF from '../../hoc/withPDF';
import useCoverSheetOverview from '../../../hooks/useCoverSheetOverview';

import CustomInfoSheet from '../custom-general/custom-info-sheet';
import CustomCoverPage from '../custom-general/custom-cover-page';
import ExecutiveSummary from '../custom-visual-inspection/executive-summary';
import OverviewSheet from '../custom-general/overview-sheet';


import * as models from '../../../utils/models';
import * as utils from '../../../utils/utilities';
import PDOSummary from './pdoSummary';
import CustomGeneralPhotos from '../custom-general/custom-general-photos';
import Conditional from '../../common/conditional';
import PDOAnomalyReport from './pdoAnomalyReport';

interface Props {
	coverData: models.ExxonMobilFVIPipingCoverData;
	executiveSummary: string;
	reportTitle: string;
	projectDetails: models.Project;
	generalPhotos: models.PDFImageProps[];
	generalChecklistItems: PDOChecklistItem[];
	vesselChecklistItems: PDOChecklistItem[];
	pipingChecklistItems: PDOChecklistItem[];
	structuresChecklistItems: PDOChecklistItem[];
}

const CustomLRUTReport: React.FC<Props> = ({
	coverData,
	executiveSummary,
	projectDetails,
	reportTitle,
	generalPhotos,
	generalChecklistItems,
	vesselChecklistItems,
	pipingChecklistItems,
	structuresChecklistItems,
}) => {
	const referenceDocuments: models.CoverDataItem[] = [
		{
			label: '1. Scope Of Work',
			value: coverData?.scopeOfWork,
		},
		{
			label: '2. Guided Wave Test Procedure',
			value: coverData?.vtProcedure,
		},
		{
			label: '3. Visual Testing Procedure',
			value: coverData?.exxonMobilWorkProcedure,
		},
		{
			label: '4. Rope Access Code of Practice',
			value: coverData?.ropeAccessCodeOfPractice,
		},
		{
			label: '5. RusselSmith Test Procedure',
			value: coverData?.russelsmithProcedure ?? '',
		},
	];

	const standardCriteriaItems: models.CoverDataItem[] = useMemo(() => {
		return coverData.coverData.map((item, index) => ({
			label: `${index + 1}. ${item.id}`,
			value: item.name.toString(),
		}));
	}, [coverData.coverData]);

	const overview = useCoverSheetOverview();

	return (
		<React.Fragment>
			<CustomCoverPage reportTitle={utils.getExtractedInspectionName(projectDetails.inspectionTypeName)} />

			<CustomInfoSheet
				projectDetails={projectDetails}
				workOrderNumber={coverData.workOrderNumber}
				reportTitle={reportTitle}
			/>

			<OverviewSheet
				overviewData={overview}
				referenceDocuments={referenceDocuments}
				standardCriteriaItems={standardCriteriaItems}
				projectDetails={projectDetails}
			/>

			<ExecutiveSummary summary={executiveSummary} />

			<PDOSummary
				generalChecklistItems={generalChecklistItems}
				vesselChecklistItems={vesselChecklistItems}
				pipingChecklistItems={pipingChecklistItems}
				structuresChecklistItems={structuresChecklistItems}
			/>

			<PDOAnomalyReport checklistItems={generalChecklistItems} section="General" />
			
			<PDOAnomalyReport checklistItems={vesselChecklistItems} section="Vessel & Equipment" />
			
			<PDOAnomalyReport checklistItems={pipingChecklistItems} section="Piping" />
			
			<PDOAnomalyReport checklistItems={structuresChecklistItems} section="Structures" />

			<Conditional isVisible={generalPhotos.length > 0}>
				<CustomGeneralPhotos label="General Photos" photos={generalPhotos} />
			</Conditional>
		</React.Fragment>
	);
};

export default withPDF(CustomLRUTReport);
